import { BottomTabBarHeightContext } from "@-/com.npmjs.expo-router/bottom-tabs";
import { getPrimaryNavigationBarHeight } from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import { cn } from "@-/com.npmjs.cn";
import { useContext, type ReactNode } from "react";
import type { ViewProps } from "@-/com.npmjs.react-native";
import { KeyboardStickyView } from "@-/com.npmjs.react-native-keyboard-controller";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import { ABOVE_KEYBOARD_BOTTOM_GAP_PX } from "#above-keyboard-edge-gap-px";

export type AboveKeyboardProps = {
  children?: ReactNode;
  className?: string;
  floating?: boolean;
  /** Pixels between content bottom and the keyboard top. Default `ABOVE_KEYBOARD_BOTTOM_GAP_PX`. */
  gap?: number;
  pointerEvents?: ViewProps["pointerEvents"];
  primaryNavigationStripOffset?: boolean;
};

/**
 * Anchors a UI strip flush with the top of the on-screen keyboard. When closed,
 * sits above the primary navigation strip (unless `primaryNavigationStripOffset` is false). Use `floating`
 * for portaled callers that aren't part of normal flex flow. Owns ALL edge spacing —
 * callers should not add their own padding/margin on the wrapped subtree.
 */
export function AboveKeyboard({
  children,
  className,
  floating = false,
  gap = ABOVE_KEYBOARD_BOTTOM_GAP_PX,
  pointerEvents = "box-none",
  primaryNavigationStripOffset = true,
}: AboveKeyboardProps) {
  const insets = useSafeAreaInsets();
  const tabBarFromCtx = useContext(BottomTabBarHeightContext);
  const tabBarHeight = primaryNavigationStripOffset
    ? (tabBarFromCtx ?? getPrimaryNavigationBarHeight(insets.bottom))
    : 0;

  const baseClassName = cn(
    "px-1",
    floating ? "absolute inset-x-0 bottom-0" : "bg-background",
    className,
  );

  return (
    <KeyboardStickyView
      className={baseClassName}
      offset={{ opened: tabBarHeight }}
      pointerEvents={pointerEvents}
      style={{ paddingBottom: gap }}
    >
      {children}
    </KeyboardStickyView>
  );
}
